import { inject, Injectable } from '@angular/core'
import { injectMutation, injectQuery, injectQueryClient } from '@ngneat/query'
import { HttpClient } from '@angular/common/http'
import { BehaviorSubject, map, tap } from 'rxjs'
import { Call } from './calls.service'
import { DemoProfileFormObject } from './admin/demo-profiles/admin-demo-profiles-page/admin-demo-profiles-page.component'
import { Organization } from './organizations.service'
import { queryKeys } from './query-keys'
import { DownloadsService } from './downloads.service'
import { MetricDataPoint } from './metrics.service'
import { ListParameterQueryOptional } from './list-query-options'

export type UserRole = 'USER' | 'ADMIN' | 'SUPER'

export type CalendarProviderName = 'MICROSOFT' | 'GOOGLE' | string

export type UserCalendarProvider = {
  provider: CalendarProviderName
  email: string | null
}

export type DemoProfileDto = Omit<DemoProfileFormObject, 'calls'> & { calls: number[] }

export type User = {
  calendarProviders: UserCalendarProvider[]
  id: number
  organizationId: number
  firstName: string
  lastName: string
  email: string
  role: UserRole
  useUserDisplayName: boolean
  assistantDisplayName: string | null
  hasPassword: boolean
  isSensitiveModeEnabled: boolean
  timezone: string
  locale: string
  activatedAt: Date
  deactivatedAt: Date | null
  sendUpcomingCallReminderEmail: boolean | null
  sendWeeklyDigestEmail: boolean | null
  sendDeactivationEmail: boolean
  sendDeactivationWarningEmail: boolean
  notifyWhenPostProcessingCompletes: boolean
} & CallJoinSettings

export type CallJoinSettings = {
  joinMeetingTentativelyAccepted: boolean
  joinMeetingDeclined: boolean
  joinMeetingUnresponded: boolean
  joinMeetingInternal: boolean | null
  joinMeetingExternal: boolean | null
}

export type AdminUser = User & {
  createdAt: Date
  firstLoginAt: Date | null
  lastLoginAt: Date | null
  createdBy: string
  creationReason: string
  oidcProviders: AdminOidcProvider[]
  metricDataPoints: MetricDataPoint[]
  keycloakLink: string
  organization: Organization
}

export type UserWithOrganization = User & {
  organization: Organization
}

export type MeUser = UserWithOrganization & {
  intercomAppId: string | null
  intercomHash: string | null
  environment: string
}

export type DemoProfile = {
  id: number
  name: string
  userId: number
  calls: Call[]
}

export type AdminListUser = User & {
  organization: Organization
  nextCallAt: Date | null
  previousCallAt: Date | null
  lastLoginAt: Date | null
  firstLoginAt: Date | null
  lastActiveAt: Date | null
  oidcProviders: AdminOidcProvider[]
  isActive: boolean
}

export type AdminOidcProvider = {
  calendarPushNotificationRegistrationFailureCount: number
  calendarSynchronizationFailureCount: number
  failedPreviouslyDueToInsufficientPermissions: boolean
  lastRegisteredForNotificationsAt: Date | null
  provider: string
}

export type UserQueryResult = {
  list: AdminListUser[]
  total: number
}

export type UserToBeCreated = Omit<User, 'id'>

export type UserEditFormOutput = {
  user: User
}

export type UserCreateFormOutput = {
  user: UserToBeCreated
}

export type AdminUserListQueryFilters = {
  organizationId?: number
  isActive?: boolean
  role?: UserRole
  isCalendarSynchronizationOk?: boolean
  startDate?: string
  endDate?: string
  hasLastActiveAt?: boolean
}
export type AdminUserListQuery = AdminUserListQueryFilters & ListParameterQueryOptional

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  mutation = injectMutation()
  httpClient = inject(HttpClient)
  query = injectQuery()
  queryClient = injectQueryClient()
  currentUser$ = new BehaviorSubject<MeUser | null>(null)
  downloadsService = inject(DownloadsService)

  getUsersAsAdmin(params: AdminUserListQuery) {
    return this.query({
      queryKey: queryKeys.users.base,
      queryFn: () => this.httpClient.get<UserQueryResult>('/api/users/admin', { params }),
    }).result$
  }

  searchUsers(searchQuery: string) {
    return this.httpClient.get<UserQueryResult>('/api/users/admin', {
      params: { query: searchQuery, pageSize: 20 },
    })
  }

  getUserAsAdmin(id: number, receiveUpdates = false) {
    return this.query({
      queryKey: queryKeys.users.byId(id),
      queryFn: () => this.httpClient.get<AdminUser>(`/api/users/${id}/admin`),
      refetchOnMount: receiveUpdates,
      refetchOnReconnect: receiveUpdates,
      refetchOnWindowFocus: receiveUpdates,
    }).result$
  }

  getOrganizationUsers() {
    return this.query({
      queryKey: queryKeys.organizations.users,
      queryFn: () => this.httpClient.get<User[]>(`/api/users/me/organizations-users`),
    }).result$
  }

  getUsersByOrganizationId(organizationId: number) {
    return this.query({
      queryKey: queryKeys.users.byOrganizationId(organizationId),
      queryFn: () =>
        this.httpClient.get<UserQueryResult>('/api/users/admin', {
          params: {
            organizationId,
            isActive: true,
            pageSize: 1000,
          },
        }),
    }).result$
  }

  createUser() {
    return this.mutation({
      mutationFn: (user: UserCreateFormOutput) => this.httpClient.post<User>('/api/users', user),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.base }),
    })
  }

  updateUser() {
    return this.mutation({
      mutationFn: (user: UserEditFormOutput) => this.httpClient.patch<User>(`/api/users/${user.user.id}`, user),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.base }),
    })
  }

  deleteUser() {
    return this.mutation({
      mutationFn: (userId: number) => this.httpClient.delete<void>(`/api/users/${userId}`),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.base }),
    })
  }

  getCurrentUser(skipRetries = false) {
    return this.query({
      queryKey: queryKeys.users.me,
      queryFn: () => this.fetchCurrentUser(),
      retry: skipRetries ? 0 : 3,
    }).result$
  }

  fetchCurrentUser() {
    return this.httpClient.get<MeUser>('/api/users/me').pipe(tap((user) => this.currentUser$.next(user)))
  }

  setToUsePasswordMutation() {
    return this.mutation({
      mutationFn: (userId: number) => this.httpClient.post<void>(`/api/users/${userId}/set-to-use-password`, {}),
      onSuccess: (_, userId) => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.byId(userId) }),
    })
  }

  setToNotUsePasswordMutation() {
    return this.mutation({
      mutationFn: (userId: number) => this.httpClient.post<void>(`/api/users/${userId}/set-not-to-use-password`, {}),
      onSuccess: (_, userId) => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.byId(userId) }),
    })
  }

  setSensitiveModeEnabledMutation() {
    return this.mutation({
      mutationFn: (params: { userId: number; isSensitiveModeEnabled: boolean }) =>
        this.httpClient.post<void>(`/api/users/${params.userId}/set-sensitive-mode-enabled`, {
          isSensitiveModeEnabled: params.isSensitiveModeEnabled,
        }),
      onSuccess: (_, params) => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.byId(params.userId) }),
    })
  }

  setCalendarSynchronizationFailureCountMutation() {
    return this.mutation({
      mutationFn: (params: { userId: number; failureCount: number }) =>
        this.httpClient.post<void>(`/api/users/${params.userId}/set-calendar-synchronization-failure-count`, { failureCount: params.failureCount }),
      onSuccess: (_, params) => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.byId(params.userId) }),
    })
  }

  downloadCalendarEvents(userId: number, startDate: Date, endDate: Date) {
    const fileName = `calendar-events-${userId}-${new Date().toISOString()}.json`
    return this.httpClient
      .get(`/api/users/${userId}/calendar-events`, {
        responseType: 'blob',
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, 'json')
        }),
        map(() => void 0)
      )
  }
}
